import { bakuPlan, bakuPoints } from '../pairing/acceleration.js';
import type { PairingEngine, PairingRequest } from '../pairing/types.js';
import type { TournamentEventBody } from './events.js';
import { DomainError } from './reducer.js';
import type { Game, RoundBye, Tournament } from './types.js';
import { PENDING } from './types.js';
import { isAvailable, playerHistory } from './views.js';

/** Build the engine request for the next round of a tournament. */
export function buildPairingRequest(t: Tournament, round = t.rounds.length + 1): PairingRequest {
  if (round !== t.rounds.length + 1) {
    throw new DomainError(`can only pair the next round (${t.rounds.length + 1})`);
  }
  const prev = t.rounds[t.rounds.length - 1];
  if (prev !== undefined && prev.games.some((g) => g.result.white === '*')) {
    throw new DomainError(`round ${prev.number} still has games without a result`);
  }
  const ps = t.settings.pointSystem;
  const plan =
    t.settings.acceleration === 'baku' ? bakuPlan(t.players.length, t.settings.rounds) : null;
  return {
    round,
    totalRounds: t.settings.rounds,
    pointSystem: ps,
    initialColour: t.settings.initialColour,
    players: [...t.players]
      .sort((a, b) => a.pairingNumber - b.pairingNumber)
      .map((p) => {
        const base = {
          id: p.pairingNumber,
          rating: p.rating,
          history: playerHistory(t, p),
          available: isAvailable(p, round),
        };
        if (plan === null) return base;
        const accelerationHistory: number[] = [];
        for (let r = 1; r < round; r++)
          accelerationHistory.push(bakuPoints(plan, p.pairingNumber, r, ps));
        return {
          ...base,
          acceleration: bakuPoints(plan, p.pairingNumber, round, ps),
          accelerationHistory,
        };
      }),
  };
}

/**
 * Pair the next round with an engine and return the RoundPaired event to
 * dispatch. Requested byes are added for players who asked for them.
 */
export function pairNextRound(t: Tournament, engine: PairingEngine): TournamentEventBody {
  const round = t.rounds.length + 1;
  if (round > t.settings.rounds) {
    throw new DomainError(`the tournament has only ${t.settings.rounds} rounds`);
  }
  const request = buildPairingRequest(t, round);
  const result = engine.pair(request);
  const byNumber = new Map(t.players.map((p) => [p.pairingNumber, p]));
  const idOf = (n: number): string => {
    const p = byNumber.get(n);
    if (p === undefined) throw new DomainError(`engine returned unknown pairing number ${n}`);
    return p.id;
  };
  const games: Game[] = result.pairs.map((pair, i) => ({
    board: i + 1,
    white: idOf(pair.white),
    black: idOf(pair.black),
    result: PENDING,
  }));
  const byes: RoundBye[] = result.byes.map((n) => ({ player: idOf(n), kind: 'U' }));
  for (const n of result.halfPointByes ?? []) byes.push({ player: idOf(n), kind: 'H' });
  const placed = new Set<string>([
    ...games.flatMap((g) => [g.white, g.black]),
    ...byes.map((b) => b.player),
  ]);
  for (const p of t.players) {
    if (placed.has(p.id)) continue;
    const req = p.byeRequests.find((b) => b.round === round);
    if (req !== undefined) byes.push({ player: p.id, kind: req.kind });
  }
  return { type: 'RoundPaired', round, games, byes, engine: engine.id };
}
